import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { fmtTotals, fmtMoney, STATUS_META, formatAxiosError } from "../lib/api";
import { fmtSiret } from "../lib/siret";
import { fmtPhone } from "../lib/phone";
import { Button } from "../components/ui/button";
import { ArrowLeft, Copy, Share2, Download, Upload, Trash2, FolderCog } from "lucide-react";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { ChannelChoices } from "../components/ChannelTiles";

const DOCUMENT_TYPES = { kbis: "Extrait K-bis", piece_identite: "Pièce d'identité", rib: "RIB", autre: "Autre document" };
const CLIENT_TYPE_LABELS = { pro: "Professionnel", tpe: "Pro ≤ 5 salariés", particulier: "Particulier" };

/** Miroir de ProposalAnalytics.js (« Dossier client ») avec la forme imbriquée `dossier`
 * renvoyée par /partner/proposals (documents.required/missing, sepa.done/failed/available_at). */
export default function PartnerProposalDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [uploadType, setUploadType] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deletingDocId, setDeletingDocId] = useState(null);
  const [downloadingDocId, setDownloadingDocId] = useState(null);
  const [sending, setSending] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // RIO d'une portabilité de numéro : indiqué ici, avant ou après la signature.
  const [rio, setRio] = useState("");
  const [savingRio, setSavingRio] = useState(false);
  const [group, setGroup] = useState(null);
  useDocumentTitle(p?.title ? `${p.title} · Suivi` : "Proposition");

  const fileInput = useRef(null);
  const loadDocuments = () => api.get(`/partner/proposals/${id}/documents`).then((r) => setDocuments(r.data)).catch(() => {});
  // Pièces manquantes / « Complet » sont calculés par le serveur : on relit la proposition avec les pièces.
  const reloadDossier = () => { loadDocuments(); api.get(`/partner/proposals/${id}`).then((r) => setP(r.data)).catch(() => {}); };

  useEffect(() => {
    api.get(`/partner/proposals/${id}`).then((r) => setP(r.data)).catch(() => { toast.error("Introuvable"); navigate("/partner/proposals"); });
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Tous les hooks AVANT le « if (!p) return » ci-dessous (ordre des hooks).
  useEffect(() => { setRio(p?.phone_rio || ""); }, [p?.phone_rio]);
  // Villa d'un dossier groupé : on lit le dossier pour le nommer (illisible : bandeau générique, jamais bloquant).
  const groupId = p?.group_id || null;
  useEffect(() => {
    setGroup(null);
    if (!groupId) return;
    api.get(`/partner/proposal-groups/${groupId}`).then((r) => setGroup(r.data)).catch(() => {});
  }, [groupId]);

  if (!p) return <div className="p-12 text-muted-foreground">Chargement…</div>;

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/partner/proposals/${id}/send`);
      setP(data);
      // Refusée non rouverte (email non parti) : rien n'est parti, seul l'avertissement du serveur s'affiche.
      const stillRefused = data.status === "refusée" && !data.emailed_to;
      if (!stillRefused) toast.success(data.emailed_to ? `Email envoyé à ${data.emailed_to}` : "Proposition envoyée");
      if (data.email_error) toast.warning(data.email_error);
    } catch (e) { toast.error(formatAxiosError(e)); }
    finally { setSending(false); }
  };

  // Jamais une signée (bouton masqué, 409 côté serveur) ; pièces déposées effacées avec la proposition.
  const remove = async () => {
    const linkNote = p.status === "brouillon" ? "" : " Le lien envoyé au client ne fonctionnera plus.";
    const groupNote = !p.group_id ? "" : group?.title ? ` Elle quittera le dossier « ${group.title} ».` : " Elle quittera son dossier groupé.";
    if (!window.confirm(`Supprimer définitivement cette proposition ?${linkNote}${groupNote}`)) return;
    setDeleting(true);
    try {
      await api.delete(`/partner/proposals/${id}`);
      toast.success("Proposition supprimée");
      navigate("/partner/proposals");
    } catch (e) { toast.error(formatAxiosError(e)); setDeleting(false); }
  };

  // Un RIO se remplace, il ne s'efface pas (le serveur refuse un RIO vide).
  const saveRio = async () => {
    setSavingRio(true);
    try {
      const { data } = await api.put(`/partner/proposals/${id}/phone-rio`, { rio });
      setP(data);
      // Forme normalisée du serveur : si elle ne change pas (RIO remplacé par sa variante), l'effet ne se relance pas.
      setRio(data.phone_rio || "");
      toast.success("RIO enregistré");
    } catch (e) { toast.error(formatAxiosError(e)); }
    finally { setSavingRio(false); }
  };

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await api.get(`/partner/proposals/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${(p.title || "proposition").replace(/\s+/g, "_")}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatAxiosError(e)); }
    finally { setDownloadingPdf(false); }
  };

  const downloadDoc = async (doc) => {
    setDownloadingDocId(doc.id);
    try {
      const res = await api.get(`/partner/proposals/${id}/documents/${doc.id}/file`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url; link.download = doc.original_name || doc.label || "piece"; link.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatAxiosError(e)); }
    finally { setDownloadingDocId(null); }
  };

  const deleteDoc = async (docId) => {
    if (!window.confirm("Supprimer cette pièce ?")) return;
    setDeletingDocId(docId);
    try {
      await api.delete(`/partner/proposals/${id}/documents/${docId}`);
      toast.success("Pièce supprimée");
      reloadDossier();
    } catch (e) { toast.error(formatAxiosError(e)); }
    finally { setDeletingDocId(null); }
  };

  const uploadDoc = async () => {
    if (!uploadType || !uploadFile) return toast.error("Choisissez un type et un fichier");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      fd.append("type", uploadType);
      await api.post(`/partner/proposals/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Pièce déposée");
      setUploadFile(null); setUploadType("");
      if (fileInput.current) fileInput.current.value = "";
      reloadDossier();
    } catch (e) { toast.error(formatAxiosError(e)); }
    finally { setUploading(false); }
  };

  const copyLink = () => { navigator.clipboard.writeText(p.public_url); toast.success("Lien copié"); };
  const shareLink = () => {
    if (navigator.share) { navigator.share({ title: p.title, url: p.public_url }).catch(() => {}); return; }
    window.open(`https://wa.me/?text=${encodeURIComponent(p.public_url)}`, "_blank", "noopener,noreferrer");
  };

  const meta = STATUS_META[p.status] || STATUS_META.brouillon;
  const dossier = p.dossier || { documents: { required: [], missing: [] }, sepa: { required: false } };
  const showDossier = dossier.documents.required.length > 0 || dossier.sepa.required || !!dossier.channels?.required || !!dossier.phone?.required;

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-3xl" data-testid="partner-proposal-detail">
      <button onClick={() => navigate("/partner/proposals")} data-testid="partner-detail-back" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="w-4 h-4" /> Retour aux propositions
      </button>

      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">{p.title}</h1>
          <span className={`inline-block mt-2 text-[11px] px-2 py-0.5 rounded-sm border font-medium ${meta.color}`}>{meta.label}</span>
        </div>
      </div>

      {p.group_id && (
        // Une villa groupée part avec son dossier : ni lien individuel ni « Envoyer » ici.
        <div className="bg-blue-50 border border-blue-200 text-blue-900 rounded-sm px-4 py-3 mb-6 flex flex-wrap items-center justify-between gap-3" data-testid="partner-detail-group">
          <p className="text-sm min-w-0 break-words">{group?.title ? `Fait partie du dossier « ${group.title} »` : "Fait partie d'un dossier groupé"}</p>
          <Button variant="outline" onClick={() => navigate(`/partner/groups/${p.group_id}`)} data-testid="partner-detail-group-open" className="rounded-sm min-h-[44px] bg-background">
            Ouvrir le dossier
          </Button>
        </div>
      )}

      <div className="bg-card border border-border rounded-sm p-5 sm:p-6 mb-6 space-y-3">
        <p className="text-sm">
          Client : <span className="font-medium">{p.client_name}</span>
          {p.client_type && <span className="text-muted-foreground"> — {CLIENT_TYPE_LABELS[p.client_type] || p.client_type}</span>}
          {p.client?.siret && <span className="text-muted-foreground" data-testid="partner-detail-siret"> · SIRET {fmtSiret(p.client.siret)}</span>}
        </p>
        {p.fibre_number_required && (
          <p className="text-sm" data-testid="partner-detail-fibre">
            N° de fibre : {p.fibre_number
              ? <span className="font-medium">{p.fibre_number}</span>
              : <span className="text-muted-foreground">à fournir par le client (avant sa signature)</span>}
          </p>
        )}
        {p.phone_number_required && (
          <div className="text-sm space-y-2" data-testid="partner-detail-phone">
            {p.phone_number_mode === "portabilite" && !p.phone_number ? (
              // Numéro effacé (client changé par l'équipe) : pas de RIO sans le numéro qu'il désigne (refusé par le serveur).
              <p className="text-muted-foreground">Ligne téléphonique : portabilité — numéro à indiquer (modifiez la proposition)</p>
            ) : p.phone_number_mode === "portabilite" ? (
              <>
                <p>Ligne téléphonique : portabilité du <span className="font-medium">{fmtPhone(p.phone_number)}</span></p>
                <div className="flex flex-wrap items-center gap-2">
                  <label htmlFor="partner-phone-rio" className="text-muted-foreground">RIO</label>
                  <input id="partner-phone-rio" value={rio} maxLength={20} autoComplete="off" spellCheck={false} onChange={(e) => setRio(e.target.value)} data-testid="partner-phone-rio"
                    placeholder="12 lettres ou chiffres" className="text-sm border border-border rounded-sm px-2.5 py-1.5 bg-background w-56" />
                  <button onClick={saveRio} disabled={savingRio || !rio.trim() || rio.trim() === (p.phone_rio || "")} data-testid="partner-phone-rio-save"
                    className="border border-border px-3 h-9 rounded-sm text-sm font-medium hover:border-primary hover:text-primary transition-all disabled:opacity-50">
                    {savingRio ? "Enregistrement…" : "Enregistrer le RIO"}
                  </button>
                  {!p.phone_rio && (
                    <span className="text-[11px] px-1.5 py-0.5 rounded-sm font-semibold border bg-amber-50 text-amber-700 border-amber-200" data-testid="partner-phone-rio-missing">RIO manquant</span>
                  )}
                </div>
              </>
            ) : p.phone_number_mode === "nouveau" ? (
              <p>Ligne téléphonique : nouveau numéro à attribuer</p>
            ) : (
              <p className="text-muted-foreground">Ligne téléphonique : choix à indiquer (modifiez la proposition)</p>
            )}
          </div>
        )}
        {p.signature?.annual_payment && (
          <p className="text-sm font-medium" data-testid="partner-detail-annual">
            Paiement souhaité : {p.signature.annual_payment === "tv" ? "la TV à l'année" : "toute l'offre à l'année"}
          </p>
        )}
        <p className="text-sm font-medium">{fmtTotals(p.total_one_shot, p.total_recurring, p.currency)}</p>
        {p.partner_pricing && (
          <p className="text-sm text-primary font-medium" data-testid="partner-detail-margin">
            Votre marge : {fmtMoney(p.partner_pricing.recurring)}/mois + {fmtMoney(p.partner_pricing.one_shot)} unique
          </p>
        )}

        {!p.group_id && (
          <div className="flex items-center gap-2 pt-1">
            <input readOnly value={p.public_url} data-testid="partner-detail-link" className="flex-1 text-xs border border-border rounded-sm px-2.5 py-2 bg-muted/30" />
            <button onClick={copyLink} data-testid="partner-detail-copy" title="Copier" className="p-2 text-muted-foreground hover:text-primary transition-colors"><Copy className="w-4 h-4" /></button>
            <button onClick={shareLink} data-testid="partner-detail-share" title="Partager" className="p-2 text-muted-foreground hover:text-primary transition-colors"><Share2 className="w-4 h-4" /></button>
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-2">
          {!p.group_id && (
            <Button onClick={send} disabled={sending || p.status === "acceptée"} data-testid="partner-detail-send" className="rounded-sm bg-primary hover:bg-primary/90 font-semibold">
              {sending ? "Envoi…" : p.status === "brouillon" ? "Envoyer" : "Renvoyer"}
            </Button>
          )}
          <Button variant="outline" onClick={downloadPdf} disabled={downloadingPdf} data-testid="partner-detail-pdf" className="rounded-sm">
            {downloadingPdf ? "Téléchargement…" : "PDF"}
          </Button>
          {p.status !== "acceptée" && (
            <Button variant="outline" onClick={remove} disabled={deleting} data-testid="partner-detail-delete" className="rounded-sm text-destructive hover:text-destructive">
              <Trash2 className="w-4 h-4 mr-1.5" /> {deleting ? "Suppression…" : "Supprimer"}
            </Button>
          )}
        </div>
      </div>

      {showDossier && (
        <div className="bg-card border border-border rounded-sm p-5 sm:p-6" data-testid="partner-detail-dossier">
          <div className="flex items-center gap-1.5 mb-4">
            <FolderCog className="w-4 h-4 text-primary" />
            <h3 className="font-heading text-lg font-semibold">Dossier</h3>
            {dossier.complete && (
              <span className="text-[10px] tracking-wide uppercase px-1.5 py-0.5 rounded-sm font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 ml-1">Complet</span>
            )}
          </div>

          {dossier.documents.required.length > 0 && (
            <div className="mb-5">
              <p className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground mb-2">Pièces</p>
              <div className="flex flex-wrap gap-2 mb-3">
                {dossier.documents.required.map((type) => {
                  const missing = dossier.documents.missing.includes(type);
                  return (
                    <span key={type} data-testid={`partner-doc-status-${type}`}
                      className={`text-xs px-2 py-1 rounded-sm border font-medium ${missing ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                      {DOCUMENT_TYPES[type] || type} — {missing ? "manquante" : "reçue"}
                    </span>
                  );
                })}
              </div>
              {documents.length > 0 && (
                <div className="space-y-1.5">
                  {documents.map((d) => (
                    <div key={d.id} className="flex items-center justify-between gap-3 text-sm py-1.5 border-b border-border last:border-0" data-testid={`partner-document-${d.id}`}>
                      <span className="truncate">
                        {d.label} — {d.original_name}{" "}
                        <span className="text-xs text-muted-foreground">
                          ({d.uploaded_by === "client" ? "déposée par le client" : d.uploaded_by === "partner" ? "déposée par vous" : "déposée par le vendeur"})
                        </span>
                      </span>
                      <div className="flex items-center gap-2 shrink-0">
                        <button onClick={() => downloadDoc(d)} disabled={downloadingDocId === d.id} title="Télécharger" data-testid={`partner-download-document-${d.id}`} className="text-muted-foreground hover:text-primary transition-colors disabled:opacity-50"><Download className="w-4 h-4" /></button>
                        <button onClick={() => deleteDoc(d.id)} disabled={deletingDocId === d.id} title="Supprimer" data-testid={`partner-delete-document-${d.id}`} className="text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-border">
                <select value={uploadType} onChange={(e) => setUploadType(e.target.value)} data-testid="partner-upload-document-type" className="text-sm border border-border rounded-sm px-2 py-1.5 bg-background">
                  <option value="">Type de pièce…</option>
                  {Object.entries(DOCUMENT_TYPES).map(([t, label]) => <option key={t} value={t}>{label}</option>)}
                </select>
                <input ref={fileInput} type="file" accept="image/*,application/pdf" onChange={(e) => setUploadFile(e.target.files?.[0] || null)} data-testid="partner-upload-document-file" className="text-sm flex-1 min-w-[10rem]" />
                <button onClick={uploadDoc} disabled={uploading} data-testid="partner-upload-document-submit"
                  className="flex items-center gap-1.5 border border-border px-3 h-9 rounded-sm text-sm font-medium hover:border-primary hover:text-primary transition-all disabled:opacity-50">
                  <Upload className="w-3.5 h-3.5" /> {uploading ? "Dépôt…" : "Déposer pour le client"}
                </button>
              </div>
            </div>
          )}

          {dossier.sepa.required && (
            <div className="pt-3 border-t border-border" data-testid="partner-detail-sepa">
              <p className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground mb-2">Mandat SEPA</p>
              <p className="text-sm">
                {dossier.sepa.done ? (
                  <span className="font-medium text-emerald-700">Signé</span>
                ) : dossier.sepa.failed ? (
                  <span className="font-medium text-rose-700">En échec</span>
                ) : dossier.sepa.available_at && new Date(dossier.sepa.available_at) > new Date() ? (
                  <>Disponible le {new Date(dossier.sepa.available_at).toLocaleDateString("fr-FR")}</>
                ) : (
                  <span className="text-muted-foreground">À signer</span>
                )}
              </p>
            </div>
          )}
          {dossier.phone?.required && (
            <div className="pt-3 border-t border-border" data-testid="partner-detail-phone-step">
              <p className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground mb-2">Portabilité du numéro</p>
              <p className="text-sm">
                {dossier.phone.done ? <span className="font-medium text-emerald-700">RIO reçu</span> : <span className="font-medium text-amber-700">RIO manquant</span>}
              </p>
            </div>
          )}
          {p.channel_choices?.length > 0 && <ChannelChoices choices={p.channel_choices} />}
        </div>
      )}
    </div>
  );
}
